import apiClient from '../utils/apiClient';

/**
 * Supplier Service
 * Handles all API calls related to suppliers
 */
const supplierService = {
  /**
   * Get all suppliers with pagination
   * @param {Number} page - Page number
   * @param {Number} limit - Number of items per page
   * @returns {Promise} - Promise resolving to suppliers data
   */
  getSuppliers: async (page = 1, limit = 10) => {
    try {
      const response = await apiClient.get(`/suppliers?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error.response?.data || { message: 'Error fetching suppliers' };
    }
  },

  /**
   * Get a single supplier by ID
   * @param {String} id - Supplier ID
   * @returns {Promise} - Promise resolving to supplier data
   */
  getSupplier: async (id) => {
    try {
      const response = await apiClient.get(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error.response?.data || { message: 'Error fetching supplier' };
    }
  },

  /**
   * Create a new supplier
   * @param {Object} supplierData - Supplier data
   * @returns {Promise} - Promise resolving to created supplier
   */
  createSupplier: async (supplierData) => {
    try {
      const response = await apiClient.post('/suppliers', supplierData);
      return response.data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      
      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }
      
      throw error.response?.data || { message: 'Error creating supplier' };
    }
  },

  /**
   * Update an existing supplier
   * @param {String} id - Supplier ID
   * @param {Object} supplierData - Updated supplier data
   * @returns {Promise} - Promise resolving to updated supplier
   */
  updateSupplier: async (id, supplierData) => {
    try {
      const response = await apiClient.put(`/suppliers/${id}`, supplierData);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier:', error);
      
      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }
      
      throw error.response?.data || { message: 'Error updating supplier' };
    }
  },

  /**
   * Delete a supplier
   * @param {String} id - Supplier ID
   * @returns {Promise} - Promise resolving to deletion result
   */
  deleteSupplier: async (id) => {
    try {
      const response = await apiClient.delete(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error.response?.data || { message: 'Error deleting supplier' };
    }
  }
};

export default supplierService;
